import React, { useMemo } from "react";
import { format } from "date-fns";

function normalize(value, fallback = "-") {
  if (value === 0) return "0";
  const text = String(value ?? "").trim();
  return text || fallback;
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return normalize(value);
  return format(date, "dd.MM.yyyy HH:mm");
}

function parseFullDialog(fullDialog) {
  if (!fullDialog) return [];

  const normalized = String(fullDialog).replace(/\r\n/g, "\n");
  const pattern = /(\d{2}\.\d{2}\.\d{2}\s+\d{2}-\d{2})\s+\[([^\]]+)\]:\s*([\s\S]*?)(?=(?:\n\d{2}\.\d{2}\.\d{2}\s+\d{2}-\d{2}\s+\[[^\]]+\]:)|$)/g;
  const messages = [];
  let match;

  while ((match = pattern.exec(normalized)) !== null) {
    messages.push({
      datetime: (match[1] || "").trim(),
      sender: (match[2] || "").trim(),
      text: (match[3] || "").trim(),
    });
  }

  return messages;
}

function isCompanySender(sender) {
  const normalized = String(sender || "").toLowerCase();
  return normalized.includes("kia qazaqstan") || normalized.startsWith("kia");
}

const DetailItem = ({ label, value }) => (
  <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
      {label}
    </p>
    <p className="mt-1 break-words text-sm font-medium text-slate-900 dark:text-white">
      {normalize(value)}
    </p>
  </div>
);

const LeadModal = ({ lead, onClose }) => {
  const messages = useMemo(() => parseFullDialog(lead?.full_dialog), [lead?.full_dialog]);

  if (!lead) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" role="dialog" aria-modal="true">
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-slate-950 dark:text-white">
              {normalize(lead.name, "Детали лида")}
            </h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {formatDateTime(lead.datetime)} / ID {normalize(lead.id)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-10 rounded-lg border border-slate-300 px-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900/20 dark:border-slate-700 dark:text-slate-100 dark:hover:bg-slate-800"
          >
            Закрыть
          </button>
        </div>

        <div className="grid min-h-0 flex-1 overflow-hidden lg:grid-cols-[360px_1fr]">
          <aside className="overflow-y-auto border-b border-slate-200 p-5 dark:border-slate-800 lg:border-b-0 lg:border-r">
            <div className="grid gap-3">
              <DetailItem label="Город" value={lead.city} />
              <DetailItem label="Модель" value={lead.selected_car} />
              <DetailItem label="Качество" value={lead.client_quality} />
              <DetailItem label="Телефон / мессенджер" value={lead.messenger} />
              <DetailItem label="Дилер" value={lead.dealer_center} />
              <DetailItem label="Повторные обращения" value={lead.Dialog_repeat ?? lead.dialog_repeat ?? 0} />
              <DetailItem label="Встреча" value={lead.meeting} />
              <DetailItem label="ID платформы" value={lead.platform_user_id} />
              {lead.dialog_link ? (
                <a
                  href={lead.dialog_link}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-10 items-center justify-center rounded-lg bg-slate-950 px-3 text-sm font-semibold text-white transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900/20 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
                >
                  Открыть CRM
                </a>
              ) : null}
            </div>
          </aside>

          <main className="min-h-0 overflow-y-auto p-5">
            <section className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
              <h3 className="text-sm font-semibold text-slate-950 dark:text-white">Резюме</h3>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700 dark:text-slate-200">
                {normalize(lead.summary_dialog, "Резюме не сохранено.")}
              </p>
            </section>

            <section className="mt-5">
              <h3 className="text-sm font-semibold text-slate-950 dark:text-white">Полный диалог</h3>
              <div className="mt-3 space-y-3">
                {messages.length > 0 ? (
                  messages.map((message, index) => {
                    const outgoing = isCompanySender(message.sender);
                    return (
                      <div key={`${message.datetime}-${index}`} className={`flex ${outgoing ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[82%] rounded-lg px-3 py-2 text-sm shadow-sm ${
                          outgoing
                            ? "bg-blue-50 text-blue-950 dark:bg-blue-950/60 dark:text-blue-100"
                            : "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100"
                        }`}>
                          <div className="mb-1 flex items-center justify-between gap-3 text-[11px] text-slate-500 dark:text-slate-400">
                            <span className="font-medium">{message.sender}</span>
                            <span>{message.datetime}</span>
                          </div>
                          <p className="whitespace-pre-wrap leading-5">{message.text}</p>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="whitespace-pre-wrap rounded-lg border border-slate-200 bg-white p-4 text-sm leading-6 text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
                    {normalize(lead.full_dialog, "Полный диалог не сохранен.")}
                  </p>
                )}
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
};

export default LeadModal;
